/**
 * Recruitment Constants
 *
 * Defines AP costs and metadata for each recruitment source.
 * Used by the UI to display costs and by the action system to validate.
 */

import type { RecruitmentSource } from '@/types'

/**
 * Recruitment source configuration including AP costs.
 */
export interface RecruitmentSourceConfig {
  key: RecruitmentSource
  label: string
  description: string
  apCost: number
  icon: string
  /** If true, must select target party/candidate */
  requiresTarget?: boolean
}

/**
 * Configuration for all recruitment sources.
 * Standard recruitment costs 1 AP, poaching costs 2 AP.
 */
export const RECRUITMENT_SOURCES: Record<RecruitmentSource, RecruitmentSourceConfig> = {
  youth_wing: {
    key: 'youth_wing',
    label: 'Youth Wing',
    description: 'Energetic but inexperienced young activists',
    apCost: 1,
    icon: '🎓',
  },
  union: {
    key: 'union',
    label: 'Labor Union',
    description: 'Loyal to labor issues and working-class support',
    apCost: 1,
    icon: '🏭',
  },
  business: {
    key: 'business',
    label: 'Business',
    description: 'Resourceful but potentially distrusted by workers',
    apCost: 1,
    icon: '💼',
  },
  activist: {
    key: 'activist',
    label: 'Activist/NGO',
    description: 'Strong issue focus but narrow appeal',
    apCost: 1,
    icon: '✊',
  },
  think_tank: {
    key: 'think_tank',
    label: 'Think Tank',
    description: 'Technocratic expertise but lower charisma',
    apCost: 1,
    icon: '📚',
  },
  independent: {
    key: 'independent',
    label: 'Independent',
    description: 'High local popularity but weak party loyalty',
    apCost: 1,
    icon: '🎭',
  },
  poached: {
    key: 'poached',
    label: 'Poach Rival',
    description: 'High recognition but high disloyalty risk',
    apCost: 2,
    icon: '🔄',
    requiresTarget: true,
  },
} as const

/**
 * Get the AP cost for a recruitment source.
 */
export function getRecruitmentAPCost(source: RecruitmentSource): number {
  return RECRUITMENT_SOURCES[source].apCost
}

/**
 * Check if a recruitment source requires a target (poaching).
 */
export function requiresRecruitmentTarget(source: RecruitmentSource): boolean {
  return RECRUITMENT_SOURCES[source].requiresTarget ?? false
}
